import { 
  WidgetIOSchema, 
  InputDefinition, 
  OutputDefinition,
  Widget,
  Connection,
} from '../types/index.js';
import { widgetRegistry } from './widgetRegistry.js';

/**
 * Check if two schemas are compatible for connection
 */
export function isSchemaCompatible(
  outputSchema?: WidgetIOSchema, 
  inputSchema?: WidgetIOSchema
): boolean {
  // If no schemas defined, allow connection (backward compatibility)
  if (!outputSchema || !inputSchema) {
    return true;
  }

  // Check base type compatibility
  if (outputSchema.type !== inputSchema.type && inputSchema.type !== 'any') {
    return false;
  }

  // For objects, check if output has required input properties
  if (outputSchema.type === 'object' && inputSchema.type === 'object') {
    if (inputSchema.properties && outputSchema.properties) {
      for (const [key, inputProp] of Object.entries(inputSchema.properties)) {
        if (!outputSchema.properties[key]) {
          return false; // Required property missing
        }
        // Recursively check nested properties
        if (!isSchemaCompatible(outputSchema.properties[key], inputProp)) {
          return false;
        }
      }
    }
  }

  // For arrays, check item compatibility
  if (outputSchema.type === 'array' && inputSchema.type === 'array') {
    if (inputSchema.items && outputSchema.items) {
      return isSchemaCompatible(outputSchema.items, inputSchema.items);
    }
  }

  // Check format compatibility if specified
  if (inputSchema.format && outputSchema.format) {
    return inputSchema.format === outputSchema.format;
  }

  return true;
}

/**
 * Check if a connection between two widgets is valid
 */
export function canConnect(
  output: OutputDefinition,
  input: InputDefinition
): boolean {
  // Basic type compatibility
  if (output.type !== input.type && input.type !== 'any') {
    // Check if transformer is available
    if (!input.transformer) {
      return false;
    }
  }

  // Schema-based validation
  return isSchemaCompatible(output.schema, input.schema);
}

/**
 * Validate a connection between widgets
 */
export function validateConnection(
  sourceWidget: Widget,
  targetWidget: Widget,
  outputId: string,
  inputId: string
): { valid: boolean; reason?: string } {
  // Get widget definitions
  const sourceDef = widgetRegistry.get(sourceWidget.type);
  const targetDef = widgetRegistry.get(targetWidget.type);

  // For widget groups, use the output/input from the widget itself
  let outputDef: OutputDefinition | undefined;
  let inputDef: InputDefinition | undefined;

  if (sourceWidget.type === 'widgetGroup') {
    // For widget groups, create output definition from the widget's output
    const output = sourceWidget.outputs.find(o => o.id === outputId);
    if (output) {
      outputDef = {
        id: output.id,
        name: output.name,
        type: output.type,
        schema: output.schema,
      };
    }
  } else if (sourceDef) {
    outputDef = sourceDef.outputDefinitions.find(o => o.id === outputId);
  }

  if (targetWidget.type === 'widgetGroup') {
    // For widget groups, create input definition from the widget's input
    const input = targetWidget.inputs.find(i => i.id === inputId);
    if (input) {
      inputDef = {
        id: input.id,
        name: input.name,
        type: input.type,
        schema: input.schema,
        required: false,
      };
    }
  } else if (targetDef) {
    inputDef = targetDef.inputDefinitions.find(i => i.id === inputId);
  }

  if (!outputDef) {
    return { valid: false, reason: 'Output not found' };
  }

  if (!inputDef) {
    return { valid: false, reason: 'Input not found' };
  }

  // Check if connection is valid
  if (!canConnect(outputDef, inputDef)) {
    return { 
      valid: false, 
      reason: `Type mismatch: ${outputDef.type} → ${inputDef.type}` 
    };
  }

  // Check if input already has a connection (unless it accepts multiple)
  const existingConnection = targetWidget.inputs.find(i => i.id === inputId)?.connected;
  if (existingConnection && !inputDef.acceptsMultiple) {
    return { 
      valid: false, 
      reason: 'Input already connected (does not accept multiple connections)' 
    };
  }

  return { valid: true };
}

/**
 * Get connection suggestions between widgets
 */
export interface ConnectionSuggestion {
  source: { widgetId: string; outputId: string };
  target: { widgetId: string; inputId: string };
  score: number;
  outputName: string;
  inputName: string;
}

export function suggestConnections(widgets: Widget[]): ConnectionSuggestion[] {
  const suggestions: ConnectionSuggestion[] = [];

  for (const sourceWidget of widgets) {
    const sourceDef = widgetRegistry.get(sourceWidget.type);
    if (!sourceDef) continue;

    for (const targetWidget of widgets) {
      if (sourceWidget.id === targetWidget.id) continue;

      const targetDef = widgetRegistry.get(targetWidget.type);
      if (!targetDef) continue;

      for (const outputDef of sourceDef.outputDefinitions) {
        for (const inputDef of targetDef.inputDefinitions) {
          if (canConnect(outputDef, inputDef)) {
            const score = calculateCompatibilityScore(outputDef, inputDef);
            
            suggestions.push({
              source: { widgetId: sourceWidget.id, outputId: outputDef.id },
              target: { widgetId: targetWidget.id, inputId: inputDef.id },
              score,
              outputName: outputDef.name,
              inputName: inputDef.name,
            });
          }
        }
      }
    }
  }

  return suggestions.sort((a, b) => b.score - a.score);
}

/**
 * Calculate compatibility score between output and input
 */
function calculateCompatibilityScore(
  output: OutputDefinition,
  input: InputDefinition
): number {
  let score = 0;

  // Exact type match
  if (output.type === input.type) {
    score += 50;
  }

  // Schema compatibility
  if (output.schema && input.schema) {
    if (output.schema.format === input.schema.format) {
      score += 30;
    }
    
    // Check for matching property names in objects
    if (output.schema.properties && input.schema.properties) {
      const outputKeys = Object.keys(output.schema.properties);
      const inputKeys = Object.keys(input.schema.properties);
      const matchingKeys = outputKeys.filter(k => inputKeys.includes(k));
      score += matchingKeys.length * 10;
    }
  }

  // Name similarity (simple check)
  const outputWords = output.name.toLowerCase().split(/\s+/);
  const inputWords = input.name.toLowerCase().split(/\s+/);
  const commonWords = outputWords.filter(w => inputWords.includes(w));
  score += commonWords.length * 20;

  return score;
}

/**
 * Build a dependency graph for widgets based on connections
 */
export function buildDependencyGraph(
  widgets: Widget[],
  connections: Connection[]
): Map<string, Set<string>> {
  const graph = new Map<string, Set<string>>();

  // Initialize all widgets in graph
  widgets.forEach(widget => {
    graph.set(widget.id, new Set());
  });

  // Add dependencies based on connections
  connections.forEach(connection => {
    const dependencies = graph.get(connection.target.widgetId);
    if (dependencies) {
      dependencies.add(connection.source.widgetId);
    }
  });

  return graph;
}

/**
 * Topological sort for widget execution order
 */
export function topologicalSort(graph: Map<string, Set<string>>): string[] {
  const visited = new Set<string>();
  const result: string[] = [];

  function visit(node: string) {
    if (visited.has(node)) return;
    visited.add(node);

    const dependencies = graph.get(node);
    if (dependencies) {
      dependencies.forEach(dep => visit(dep));
    }

    result.push(node);
  }

  // Visit all nodes
  graph.forEach((_, node) => visit(node));

  return result;
}